/**
 * Filter bar for the RuntimeFsBrowser files tab — narrows which files
 * the tree shows based on upload attribution (handoff §2.2).
 *
 * Three controls: uploader dropdown (multi-select, populated by
 * GET /api/org/:orgId/cases/:caseId/uploaders OR derived locally from
 * the attribution map), source multi-select (6 chip-buttons), and an
 * optional date range. Defaults exclude `migration` (handoff §2.2 +
 * the operator's Q2 handback decision).
 *
 * Filter state lives in the parent (RuntimeFsBrowser); this is a
 * controlled component. Empty filter state ⇒ everything visible.
 */

import { X } from "lucide-react";
import { useMemo } from "react";

import { SourceChip, UPLOAD_SOURCES } from "@/components/uploads/SourceChip";
import {
  resolveUploaderDisplay,
  type ExecutionLedgerRecord,
  type UploadSource,
} from "@/types/execution-ledger";

export interface UploadFilterState {
  /** Set of `uploadedById` values to keep. Empty = no uploader filter. */
  uploaderIds: Set<string>;
  /** Set of source values to keep. Empty = no source filter (effectively
   *  shows EVERYTHING, including `migration`). The DEFAULT_FILTER below
   *  pre-populates this with all 5 non-migration sources. */
  sources: Set<UploadSource>;
  /** ISO `YYYY-MM-DD`, inclusive. Empty string = no bound. */
  fromDate: string;
  toDate: string;
}

/** Default state — all sources EXCEPT `migration`, no uploader filter,
 *  no date bounds. Matches handoff §2.2 ("`migration` excluded by
 *  default with a `Show backfill rows` toggle"). */
export const DEFAULT_UPLOAD_FILTER: UploadFilterState = {
  uploaderIds: new Set(),
  sources: new Set<UploadSource>([
    "lexide",
    "whatsapp",
    "telegram",
    "agent",
    "api",
    // migration deliberately excluded
  ]),
  fromDate: "",
  toDate: "",
};

interface Props {
  value: UploadFilterState;
  onChange: (next: UploadFilterState) => void;
  /** All records under scope — used to derive uploader options when
   *  no `/uploaders` endpoint is available (e.g. when the FsBrowser
   *  doesn't know the case context). Distinct `uploadedById`s only. */
  records: ExecutionLedgerRecord[];
  /** Truthy when the attribution map paged-out; surfaces a hint that
   *  some uploads may not be in the visible set. */
  truncated?: boolean;
}

export function UploadFiltersBar({
  value,
  onChange,
  records,
  truncated,
}: Props) {
  const uploaders = useMemo(() => collectUploaders(records), [records]);
  const showBackfill = value.sources.has("migration");
  const activeFilterCount = countActive(value);

  const toggleSource = (s: UploadSource) => {
    const next = new Set(value.sources);
    if (next.has(s)) next.delete(s);
    else next.add(s);
    onChange({ ...value, sources: next });
  };

  const toggleUploader = (id: string) => {
    const next = new Set(value.uploaderIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange({ ...value, uploaderIds: next });
  };

  return (
    <div
      className="flex flex-col gap-2 rounded-md border border-slate-200 bg-white p-2 dark:border-slate-700 dark:bg-slate-900"
      data-testid="upload-filters-bar"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-xs">
        {/* Source chip group */}
        <div className="flex items-center gap-1">
          <span className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Source
          </span>
          {UPLOAD_SOURCES.filter((s) => s !== "migration").map((s) => {
            const active = value.sources.has(s);
            return (
              <button
                key={s}
                type="button"
                onClick={() => toggleSource(s)}
                className={`rounded border px-1 py-0.5 transition-colors ${
                  active
                    ? "border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950"
                    : "border-slate-200 opacity-50 hover:opacity-100 dark:border-slate-700"
                }`}
                title={active ? `Hide ${s}` : `Show ${s}`}
                data-testid={`upload-filter-source-${s}`}
              >
                <SourceChip source={s} compact />
              </button>
            );
          })}
        </div>

        {/* Uploader multi-select (rendered as a row of pills) */}
        {uploaders.length > 0 && (
          <div className="flex flex-wrap items-center gap-1">
            <span className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Uploader
            </span>
            {uploaders.map((u) => {
              const active = value.uploaderIds.has(u.id);
              return (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => toggleUploader(u.id)}
                  className={`rounded border px-1.5 py-0.5 text-[10px] transition-colors ${
                    active
                      ? "border-indigo-300 bg-indigo-50 text-indigo-700 dark:border-indigo-700 dark:bg-indigo-950 dark:text-indigo-300"
                      : "border-slate-200 text-slate-500 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800"
                  }`}
                  title={`${u.label} (${u.count})`}
                  data-testid={`upload-filter-uploader-${u.id}`}
                >
                  {u.label} <span className="opacity-50">({u.count})</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Date range */}
        <div className="flex items-center gap-1">
          <span className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
            From
          </span>
          <input
            type="date"
            value={value.fromDate}
            onChange={(e) => onChange({ ...value, fromDate: e.target.value })}
            className="rounded border border-slate-200 bg-white px-1 py-0.5 text-[11px] dark:border-slate-700 dark:bg-slate-950"
            data-testid="upload-filter-from"
          />
          <span className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
            To
          </span>
          <input
            type="date"
            value={value.toDate}
            onChange={(e) => onChange({ ...value, toDate: e.target.value })}
            className="rounded border border-slate-200 bg-white px-1 py-0.5 text-[11px] dark:border-slate-700 dark:bg-slate-950"
            data-testid="upload-filter-to"
          />
        </div>

        {/* Show-backfill toggle (the `migration` source is excluded
            by default per handoff §2.2). */}
        <label className="ml-auto flex cursor-pointer items-center gap-1 text-[11px] text-slate-600 dark:text-slate-300">
          <input
            type="checkbox"
            checked={showBackfill}
            onChange={() => toggleSource("migration")}
            className="h-3 w-3"
            data-testid="upload-filter-show-backfill"
          />
          Show backfill rows
        </label>

        {activeFilterCount > 0 && (
          <button
            type="button"
            onClick={() => onChange(DEFAULT_UPLOAD_FILTER)}
            className="inline-flex items-center gap-1 rounded border border-slate-200 px-1.5 py-0.5 text-[10px] text-slate-500 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800"
            data-testid="upload-filter-reset"
          >
            <X className="h-2.5 w-2.5" /> Reset
          </button>
        )}
      </div>

      {truncated && (
        <p className="text-[10px] italic text-amber-700 dark:text-amber-400">
          Showing the most recent 2,000 uploads — older rows may not be
          attributed in the tree.
        </p>
      )}
    </div>
  );
}

interface UploaderOption {
  id: string;
  label: string;
  count: number;
}

/** Derive uploader options from the in-memory records (each distinct
 *  `uploadedById` + its count + the operator-facing label). Sorted by
 *  count DESC so the high-frequency uploaders are easy to find. */
function collectUploaders(records: ExecutionLedgerRecord[]): UploaderOption[] {
  const byId = new Map<string, UploaderOption>();
  for (const r of records) {
    if (!r.uploadedById) continue;
    const existing = byId.get(r.uploadedById);
    if (existing) {
      existing.count += 1;
    } else {
      const resolved = resolveUploaderDisplay(
        r.uploadedById,
        r.uploadedByEmail,
      );
      byId.set(r.uploadedById, {
        id: r.uploadedById,
        label: resolved.label,
        count: 1,
      });
    }
  }
  return Array.from(byId.values()).sort((a, b) => b.count - a.count);
}

function countActive(f: UploadFilterState): number {
  let n = 0;
  // Source filter is considered "active" if it deviates from the
  // default (5/6 non-migration). Exact match against the default set
  // → not counted as active.
  const defaultSrcs = DEFAULT_UPLOAD_FILTER.sources;
  const sameAsDefault =
    f.sources.size === defaultSrcs.size &&
    Array.from(f.sources).every((s) => defaultSrcs.has(s));
  if (!sameAsDefault) n += 1;
  if (f.uploaderIds.size > 0) n += 1;
  if (f.fromDate) n += 1;
  if (f.toDate) n += 1;
  return n;
}

/** Decide whether a given record passes the active filter. Used to
 *  filter the FsBrowser tree's file rows. */
export function recordPassesFilter(
  record: ExecutionLedgerRecord,
  filter: UploadFilterState,
): boolean {
  if (filter.uploaderIds.size > 0 && record.uploadedById) {
    if (!filter.uploaderIds.has(record.uploadedById)) return false;
  }
  if (filter.sources.size > 0 && record.uploadSource) {
    if (!filter.sources.has(record.uploadSource as UploadSource)) return false;
  }
  if (filter.fromDate && record.uploadedAt) {
    if (record.uploadedAt.slice(0, 10) < filter.fromDate) return false;
  }
  if (filter.toDate && record.uploadedAt) {
    if (record.uploadedAt.slice(0, 10) > filter.toDate) return false;
  }
  return true;
}
